import { calculateAge } from "../utils/dates";
import { CategoryWithId } from "../interfaces/ICategories";
import { ParticipantWithId } from "../interfaces/IParticipants";
import { useMemo } from "react";

export interface AgeValidationResult {
  isValid: boolean;
  exceededParticipants: ParticipantWithId[];
  exceededPercentage: number;
  maxAllowedParticipants: number;
  totalParticipants: number;
}

/**
 * Hook do walidacji limitów wiekowych w prezentacjach
 * Sprawdza czy procent uczestników wykraczających wiekiem nie przekracza dozwolonego limitu
 */
export const useAgeValidation = (
  participants: ParticipantWithId[],
  category: CategoryWithId | null
): AgeValidationResult => {
  return useMemo(() => {
    // Jeśli brak kategorii lub brak limitu odstępstwa, walidacja przechodzi
    if (!category || category.ageDeviationPercentage === 0) {
      return {
        isValid: true,
        exceededParticipants: [],
        exceededPercentage: 0,
        maxAllowedParticipants: 0,
        totalParticipants: participants.length,
      };
    }

    // Uruchamiaj walidację dopiero gdy dodano min. liczbę osób wymaganą przez kategorię
    if (participants.length < category.minPresentationSize) {
      return {
        isValid: true,
        exceededParticipants: [],
        exceededPercentage: 0,
        maxAllowedParticipants: 0,
        totalParticipants: participants.length,
      };
    }

    // Pobierz daty graniczne kategorii
    const minBirthDate = category.minParticipantBirthDate;
    const maxBirthDate = category.maxParticipantBirthDate;

    // Znajdź uczestników starszych od maksymalnej daty urodzenia kategorii (urodzeni PRZED minBirthDate)
    const olderParticipants = participants.filter((participant) => {
      if (!minBirthDate) return false;
      return new Date(participant.birthDate) < new Date(minBirthDate);
    });

    // Znajdź uczestników młodszych od maksymalnej daty urodzenia kategorii (urodzeni PO maxBirthDate)
    const youngerParticipants = participants.filter((participant) => {
      if (!maxBirthDate) return false;
      return new Date(participant.birthDate) > new Date(maxBirthDate);
    });

    // Znajdź uczestników w zakresie wiekowym kategorii
    const inRangeParticipants = participants.filter((participant) => {
      const participantBirthDate = new Date(participant.birthDate);
      const isAfterMin = !minBirthDate || participantBirthDate >= new Date(minBirthDate);
      const isBeforeMax = !maxBirthDate || participantBirthDate <= new Date(maxBirthDate);
      return isAfterMin && isBeforeMax;
    });

    // Oblicz maksymalną dozwoloną liczbę starszych uczestników (odstępstwo)
    // Zaokrąglone w dół, może być 0
    const maxAllowedOlderParticipants = Math.floor(
      (participants.length * category.ageDeviationPercentage) / 100
    );

    // Sprawdź czy limit starszych uczestników nie został przekroczony
    const isOlderParticipantsLimitExceeded = olderParticipants.length > maxAllowedOlderParticipants;

    // Walidacja przechodzi jeśli limit starszych uczestników nie został przekroczony
    const isValid = !isOlderParticipantsLimitExceeded;

    // Oblicz procent starszych uczestników dla wyświetlenia
    const olderParticipantsPercentage =
      participants.length > 0 ? (olderParticipants.length / participants.length) * 100 : 0;

    return {
      isValid,
      exceededParticipants: olderParticipants,
      exceededPercentage: olderParticipantsPercentage,
      maxAllowedParticipants: maxAllowedOlderParticipants,
      totalParticipants: participants.length,
    };
  }, [participants, category]);
};
